import React, { useEffect, useState } from "react";
import { Download, FileDown } from "lucide-react";
import { Input } from "antd";
import {
  useAllOrders,
  useSearchOrders,
  useDownloadLabel,
  useDownloadManifest,
  useTrackOrder,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const PickUpPending = ({ status = "pickup" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState([]);

  const { setLoading } = useLoader();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
    search: searchQuery,
  });

  const { mutate: downloadLabel, isLoading: downloading } = useDownloadLabel();
  const { mutate: downloadManifest, isLoading: downloadingManifest } =
    useDownloadManifest();
  const { mutate: trackOrder } = useTrackOrder();

  // Debounce Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  // ✅ FIXED — Select All stores full object with shipment_id
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(
        orders.map((order) => ({
          awbNumber: order.awbNumber,
          shipment_id: order.shipment_id,
          full: order,
        }))
      );
    } else {
      setSelectedOrders([]);
    }
  };

  // ✅ FIXED — Select Single
  const handleSelectOne = (order) => {
    setSelectedOrders((prev) =>
      prev.some((o) => o.shipment_id === order.shipment_id)
        ? prev.filter((o) => o.shipment_id !== order.shipment_id)
        : [
            ...prev,
            {
              awbNumber: order.awbNumber,
              shipment_id: order.shipment_id,
              full: order,
            },
          ]
    );
  };

  // ✅ FIXED — Bulk Label Download
  const handleDownloadAll = () => {
    const shipmentIds = selectedOrders.map((o) => o.shipment_id); // Extract only shipment IDs
    downloadLabel(shipmentIds);
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search + Download All */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />

        {selectedOrders.length > 0 && (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            onClick={handleDownloadAll}
            disabled={downloading}
          >
            Download Selected ({selectedOrders.length})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={
                    orders.length > 0 &&
                    orders.every((order) =>
                      selectedOrders.some(
                        (o) => o.shipment_id === order.shipment_id
                      )
                    )
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier Details</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedOrders.some(
                        (o) => o.shipment_id === order.shipment_id
                      )}
                      onChange={() => handleSelectOne(order)}
                    />
                  </td>

                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>

                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>

                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>

                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>

                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>

                  <td className="p-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      {order?.courier}
                    </p>

                    {order.awbNumber ? (
                      <a
                        onClick={() => trackOrder(order.awbNumber)}
                        target="_blank"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Track Order
                      </a>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded">
                        AWB Not Available
                      </span>
                    )}
                  </td>

                  <td className="p-4 space-x-2">
                    {order.shipment_id && (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                        onClick={() => downloadLabel([order.shipment_id])}
                        disabled={downloading}
                      >
                        <Download size={16} />
                      </button>
                    )}

                    {order.shipment_id && (
                      <button
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm"
                        onClick={() => downloadManifest([order.shipment_id])}
                        disabled={downloadingManifest}
                      >
                        <FileDown size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && orders.length > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>

          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Previous
            </button>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border rounded bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickUpPending;
